/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { resources, defaultNS, fallbackLng, supportedLngs } from '../locales';

/**
 * i18next instance configured for ZIA Platforms Web.
 *
 * Uses LanguageDetector to detect the user's locale (no Redux involved).
 * Detection order: query string `?lang=` → localStorage `zia-locale` → browser navigator.
 * Detected locale is persisted to localStorage under the `zia-locale` key.
 *
 * Initialization is fire-and-forget (`void`). The instance is ready
 * synchronously for all practical purposes because no async backend loader
 * is involved — resources are bundled inline via the `resources` map from
 * `../locales`. Consumers should import `i18n` from this module rather than
 * from `i18next` directly to ensure they receive the configured instance.
 */
void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    fallbackLng,
    supportedLngs: [...supportedLngs],
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'zia-locale',
      caches: ['localStorage'],
    },
  });

/**
 * Configured i18next instance for use throughout ZIA Platforms Web.
 *
 * Import this value instead of the bare `i18next` default export to guarantee
 * the LanguageDetector and initReactI18next plugins are already registered.
 * Pass this instance to `I18nextProvider` in the application root and in test
 * wrappers to ensure all components share the same initialized instance.
 *
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export const i18n = i18next;
